import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';

export const getAssetId = (asset) => asset?.dataproviderassigneduniqueid || asset?.id;

export const hasSameOwnerAndContactDetails = (site, assetSuppData) => {
    if (!assetSuppData) {
        return false;
    }

    const hasSameOwner = site?.nuarActor?.systemId === assetSuppData?.nuarActor?.systemId;
    const hasSameContactDetails = isEqual(site?.contacts, assetSuppData?.contacts);

    return hasSameOwner && hasSameContactDetails;
};

export const arrayHasUniqueItems = (array, { keysToCompare } = {}) => {
    if (!array || array.length < 2) {
        return false;
    }
    const [baseline, ...rest] = array;
    if (keysToCompare) {
        return rest.some(item => {
            return !isEqual(
                keysToCompare.reduce((acc, key) => ({ ...acc, [key]: item[key] }), {}),
                keysToCompare.reduce((acc, key) => ({ ...acc, [key]: baseline[key] }), {})
            );
        });
    }
    return rest.some(item => !isEqual(item, baseline));
};

export const useAssetContactDetails = (assets) => {
    const { layers } = useSelector(state => state.assets);

    const supplementaryData = useSelector(state => state?.supplementaryData?.data || {});
    const assetErrors = useSelector(state => state?.supplementaryData?.assetErrors || {});
    const assetIds = assets.map(getAssetId).filter(Boolean);
    // Scoped to just the assets rendered in this box, not the legacy global supplementaryData.error
    // flag (which is only ever set by the bulk enhanced-measures retry path)
    const error = assetIds.some(id => assetErrors[id]);
    const isPending = !error && assetIds.some(id => !supplementaryData[id]);
    const allFeatures = Object.keys(layers).map(key => layers[key]).flat();

    function getFeatureForAssetId (siteId) {
        return allFeatures.find(feature => feature?.getProperties()?.dataproviderassigneduniqueid === siteId);
    }

    const contactDetails = assets.filter((site, index) => {
        const siteId = getAssetId(site);
        if (site.feature || !siteId || !supplementaryData[siteId]) {
            // Don't dedupe if this site has a feature or we haven't loaded supp. data yet
            return true;
        }

        const firstInstanceOfRedactedAssetWithContactDetailsIndex = assets.findIndex(asset => {
            // Return true if this is the FIRST INSTANCE of a redacted asset with identical owner and contact details.
            // This means we only will have 1 entry in the list per unique contact details (even if a duplicate of those with a feature)

            const assetId = getAssetId(asset);
            const assetSuppData = assetId ? supplementaryData[assetId] : null;

            if (!hasSameOwnerAndContactDetails(site, assetSuppData)) {
                // only return when identical owner + contact details are present
                return false;
            }

            const featureForSensitiveAssetComparingTo = assetId ? getFeatureForAssetId(assetId) : null;
            // want to group redacted assets only so only return true if same details (true if skipped above return) AND does not have a feature..
            return !featureForSensitiveAssetComparingTo;
        });
        // Only include first instance of redacted asset
        return index === firstInstanceOfRedactedAssetWithContactDetailsIndex;
    }).sort((a, b) => (a.nuarActor?.label || '').localeCompare(b.nuarActor?.label || ''));

    const hasMultipleContactDetails = arrayHasUniqueItems(contactDetails, { keysToCompare: ['contacts'] });
    return {
        contactDetails,
        hasMultipleContactDetails,
        isPending,
        error
    };
};
